import { useGeoStore } from '@/stores/geo';
import { useLocationStore } from '@/stores/location';
import { useMrxStore } from '@/stores/mrx';
import { useTrackStore } from '@/stores/track';
import { buildEncounterDetails } from '@/utils/encounterDetails';
import { gatherResponses } from '@/utils/panelResponses';

export interface CollectPayloadOptions {
  deIdentify?: boolean;
  clinicalNoteText?: string | null;
  encounterId?: string | null;
}

function deIdentifyPatient(patient: Record<string, any>, identifyingPanels: string[]) {
    const fields: Record<string, string> = {
        age: 'age',
        ageInDays: 'age',
        gender: 'gender',
        name: 'patient_name',
        patientId: 'patient_id',
        phoneNumber: 'patient_phone',
        birthDate: 'age'
    };

    return Object.fromEntries(
        Object.entries(patient).map(([key, value]) => [
            key,
            identifyingPanels.includes(fields[key]) ? undefined : value
        ])
    );
}

// an absent or blank note is omitted from the payload, not sent as empty
function mapClinicalNoteText(clinicalNoteText?: string | null): string | undefined {
    if (clinicalNoteText == null) {
        return undefined;
    }

    const trimmedText = String(clinicalNoteText).trim();
    if (!trimmedText) {
        return undefined;
    }

    return trimmedText;
}

// an unknown track must not read as "nothing is identifying": that uploads names un-redacted
export function identifyingPanels(): string[] {
    const trackStore = useTrackStore();
    const details = buildEncounterDetails();
    const track = trackStore.all.find((t: any) => t.id === details.track.id) || trackStore.current;
    if (!Array.isArray(track?.panels)) return [];
    return track.panels
        .filter((panel: any) => panel.options && panel.options.identifying === true)
        .map((panel: any) => panel.name);
}

export async function buildCollectPayload(
    { deIdentify = true, clinicalNoteText, encounterId }: CollectPayloadOptions = {},
): Promise<Record<string, any>> {
    var results: Record<string, any> = {}

    const locationStore = useLocationStore();
    const locations = locationStore.responses;
    const bundle = useMrxStore().bundleSnapshot;
    let responses = await gatherResponses(useTrackStore().current)
    let details = buildEncounterDetails()
    let assessment = bundle.filter((item: any) => (item.type === 'assessment'))
    let recommendations = bundle.filter((item: any) => (item.type === 'plan'))
    let medicationRequests = bundle.filter((item: any) => (item.type === 'plan' && item.category === 'medication' && item.shown === true))
    let panels = identifyingPanels();

    let mappedResponses = responses.map((c: any) => {
        return {
            name: c.name,
            value: (deIdentify && panels.includes(c.name)) ? null : String(c.value),
            panel: c.panel_name,
            token: c.name === c.panel_name ? `questions.${c.name}.title` : `responses.${c.name}.title`,
            panel_type: c.datapoint,
            panel_input: c.panel_input
        }
    })

    let mappedLocations: any[] = [];
    if (locations && Object.keys(locations).length > 0) {
        mappedLocations = Object.values(locations).map((c: any) => {
            return {
                name: c.name,
                token: c.token,
                value: c.value,
                location_type: c.location_type
            }
        })
    }

    let mappedAssessments = assessment.map((c: any) => {
        return {
            name: c.name,
            severity: c.severity,
            score: c.score,
            overridden: c.overridden || null,
            selected: c.selected || false,
            triage: c.triage_recommendation
        }
    })

    results = Object.assign({}, details);

    results.id = encounterId || results.id.id;
    results.engagement_code = results.encounter.code;
    results.completed_date = results.timing.end;
    results.responses = mappedResponses;
    results.locations = mappedLocations;
    results.assessments = mappedAssessments;

    const mappedClinicalNoteText = mapClinicalNoteText(clinicalNoteText);
    if (mappedClinicalNoteText) {
        results.clinical_note_text = mappedClinicalNoteText;
    }

    if (deIdentify) {
        results.patient = deIdentifyPatient(results.patient, panels);
    }

    if (recommendations.length > 0) {
        results.recommendations = recommendations.map((rec: any) => {
            return {
                category: rec.category,
                name: rec.name,
                assessment: rec.assessment,
                severity: rec.severity,
                shown: rec.shown || false,
            }
        })
    }

    if (medicationRequests.length > 0) {
        // 1. Create a temporary object to hold our grouped medications.
        const groupedMeds: Record<string, any> = {};

        // 2. Loop through each medication request one by one.
        medicationRequests.forEach((medRequest: any) => {
            const name = medRequest.name;

            // 3. If we haven't seen this medication before, create its entry.
            if (!groupedMeds[name]) {
                // Find the corresponding treatment info
                const treatmentBundle: Record<string, any> =
                    bundle.find((item: any) => item.type === 'treatment' && item.name === name) || {};

                // get extended entries
                const filterKeys = ['assessment', 'delivery', 'group', 'name', 'order', 'priority', 'severity', 'treatmentFlags', 'type']
                const extended = Object.entries(treatmentBundle)
                    .filter(([key]) => !filterKeys.includes(key))
                    .map(([key, value]) => ({
                        name: key,
                        value: String(value),
                    }))

                groupedMeds[name] = {
                    name: name,
                    assessments: [], // Start with an empty list of assessments
                    shown: medRequest.shown || false,
                    selected: medRequest.selected || false,
                    group: treatmentBundle?.group || null,
                    priority: treatmentBundle?.priority || null,
                    order: treatmentBundle?.order || null,
                    extended,
                };
            }

            // 4. Add the current assessment details to its group.
            groupedMeds[name].assessments.push({
                assessment: medRequest.assessment,
                severity: medRequest.severity
            });
        });

        // 5. Convert the object of grouped medications back into an array.
        results.medicationRequests = Object.values(groupedMeds);
    }

    const geoStore = useGeoStore();
    results.geo = geoStore.$state;

    results.etl = {
        version: '6.0'
    }

    results.dataContext = `flexi-${details.encounter.env}`

    return results
}
